import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Modal,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Switch,
    FormControlLabel,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Alert,
    Tabs,
    Tab,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Slider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Chip,
    Stack
} from '@mui/material';
import DocumentPreview from './DocumentPreview';
import {
    Visibility,
    Save,
    Refresh,
    Close as CloseIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    Description as DescriptionIcon,
    ViewColumn as ViewColumnIcon,
    Upload as UploadIcon,
    TextIncrease as TextIncreaseIcon,
    DesignServices as DesignServicesIcon,
    Add as AddIcon,
    Draw as DrawIcon,
    OpenInNew as OpenInNewIcon,
    BusinessCenter as BusinessCenterIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'react-signature-canvas';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import html2canvas from 'html2canvas';
import { GetDocumentSettings, UpdateDocumentSettings } from '../../../Services/documentSettings.service';
import DocumentService from '../../../Services/documents.service';
import ClientService from '../../../Services/client.service.js';
import ClientItemsService from '../../../Services/clientItems.service';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import DraggableModal from '../../DraggableModal/DraggableModal.js';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import { normalizeDocumentSettings, DEFAULT_DOCUMENT_SETTINGS } from '../../../Utils/documentSettingsDefaults';
import ClientItemCreateModal from '../../ClientItems/SubComponents/ClientItemCreateModal.js';
import Swal from 'sweetalert2';

// Backend DocumentTypes enum mapping for preview only
const DocumentTypesEnum = [
    { value: 'TaxInvoice', label: "חשבונית מס" },
    { value: 'ReceiptTax', label: "חשבונית מס קבלה" },
    { value: 'CreditNote', label: "חשבונית מס זיכוי" },
    { value: 'Receipt', label: "קבלה" },
    { value: 'Quotation', label: "הצעת מחיר" },
    { value: 'Order', label: "הזמנה" },
    { value: 'Transaction', label: "חשבון עסקה" },
    { value: 'PostponedReceipt', label: "קבלת דחויים" },
    { value: 'Deposit', label: "משיכה/הפקדה" },
];



const StyledButton = styled(Button)(({ theme, variant = 'contained', direction, size = 'medium' }) => ({
    borderRadius: '12px',
    padding: size === 'small' ? '6px 8px' :
        size === 'medium' ? '6px 8px' :
            '8px 16px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: size === 'small' ? '11px' :
        size === 'medium' ? '12px' :
            '13px',
    minHeight: size === 'small' ? '24px' :
        size === 'medium' ? '30px' :
            '36px',
    transition: 'all 0.3s ease',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },

}));

const StyledCard = styled(Card)(({ theme, direction }) => ({
    background: '#ffffff',
    border: `1px solid #e8eaed`,
    borderRadius: '16px',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    '&:hover': {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

const StyledAccordion = styled(Accordion)(({ theme, direction }) => ({
    borderRadius: '16px',
    border: `1px solid #e8eaed`,
    overflow: 'hidden',
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    '&:before': {
        display: 'none',
    },
    '&.Mui-expanded': {
    },
    '& .MuiAccordionSummary-root': {
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e8eaed',
        padding: '0 16px',
        '&:hover': {
            backgroundColor: '#f1f3f4',
        },
    },
    '& .MuiAccordionSummary-content': {
    },
    '& .MuiAccordionDetails-root': {
        padding: '10px',
        backgroundColor: '#ffffff',
    },
}));

const StyledSlider = styled(Slider)(({ theme, direction }) => ({
    direction: direction === 'rtl' ? 'rtl' : 'ltr',
    '& .MuiSlider-track': {
        background: `linear-gradient(90deg, #4285f4 0%, #3367d6 100%)`,
        height: '6px',
        borderRadius: '3px',
    },
    '& .MuiSlider-rail': {
        height: '6px',
        borderRadius: '3px',
        backgroundColor: '#e8eaed',
    },
    '& .MuiSlider-thumb': {
        backgroundColor: '#4285f4',
        width: '20px',
        height: '20px',
        border: '3px solid #ffffff',
        boxShadow: '0 2px 8px rgba(66, 133, 244, 0.3)',
        '&:hover': {
            boxShadow: `0 0 0 8px rgba(66, 133, 244, 0.2)`,
        },
    },
    '& .MuiSlider-mark': {
        backgroundColor: '#4285f4',
        width: '2px',
        height: '10px',
    },
    '& .MuiSlider-markLabel': {
        color: '#5f6368',
        fontSize: '12px',
        fontWeight: 500,
        direction: direction === 'rtl' ? 'rtl' : 'ltr',
    },
}));

const DocumentSettingsDialog = ({
    open,
    onClose,
    onSave,
    token,
    accountantId,
    caseId,
    direction,
    client
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [activeTab, setActiveTab] = useState(0);

    // Document settings state - simplified to one settings object per case
    const [settings, setSettings] = useState({
        customDescription: '',
        customFooter: '',
        headerRightText: '',
        headerLeftText: '',
        itemsDisplayMode: '',
        columnSettings: {
            textSize: 11,
            companyInfoTextSize: 16,
            documentTitleTextSize: 20,
            profilePictureShape: 'NoChooseShape',
            boldDate: false,
            width: 80,
            height: 100,
            backgroundColor: '#ffffff',
            textColor: '#000000',
            upperPanelBackgroundColor: '#fbfbfb',
            svgColor: '#F9AA2A',
            svgShape: 'corner',
            upperPanelColor: '#fbfbfb',
            svgImageColor: '#F9AA2A',
            logoPosition: 'left',
            totalsLayout: 'horizontal', // 'horizontal' or 'vertical'
            dateAlign: 'left', // 'left' or 'right'
            titleTextColor: '#000000',
            documentFont: 'Montserrat',
        }
    });

    const [selectedDocumentType, setSelectedDocumentType] = useState('TaxInvoice'); // Default to TaxInvoice for preview only
    const iframeRef = useRef(null);

    // Signature and Profile Picture states
    const [signaturePreview, setSignaturePreview] = useState('');
    const [profilePreview, setProfilePreview] = useState('');
    const [newSignatureFile, setNewSignatureFile] = useState(null);
    const [newProfileFile, setNewProfileFile] = useState(null);
    const [signatureModified, setSignatureModified] = useState(false);
    const [profileModified, setProfileModified] = useState(false);
    const [addSignaturePad, setAddSignaturePad] = useState(false);
    const [textSignatureOption, setTextSignatureOption] = useState(false);
    const [textSignature, setTextSignature] = useState('');
    const [selectedFont, setSelectedFont] = useState('Arial');

    // HTML preview state
    const [previewMode, setPreviewMode] = useState('html'); // 'html' or 'pdf'
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null);

    // DraggableModal state variables
    const [openedIframeUrl, setOpenedIframeUrl] = useState('');
    const [bigIframeModalOpen, setBigIframeModalOpen] = useState(false);

    // Default item for documents (client items + current default)
    const [clientItems, setClientItems] = useState([]);
    const [defaultItem, setDefaultItem] = useState(null);
    const [selectedItemMakat, setSelectedItemMakat] = useState('');
    const [defaultItemLoading, setDefaultItemLoading] = useState(false);
    const [createItemModalOpen, setCreateItemModalOpen] = useState(false);
    const [createItemLoading, setCreateItemLoading] = useState(false);

    const sigPad = useRef();

    
    const fonts = [
        'Arial', 'Courier New', 'Comic Sans MS', 'Georgia', 'Times New Roman', 'Verdana',
        'Trebuchet MS', 'Tahoma', 'Helvetica', 'Impact', 'PT Sans Narrow'
    ];
    const documentFontOptions = ['Montserrat', ...fonts];

    // DraggableModal functions
    const openPdfInNewTab = (pdfUrl) => {
        if (pdfUrl) {
            setOpenedIframeUrl(pdfUrl ? pdfUrl : NoDocumentsPdf);
            setBigIframeModalOpen(true);
        }
    };

    const handleCloseIframeModal = () => {
        setBigIframeModalOpen(false);
        setOpenedIframeUrl('');
    };
    // Helper function to check if document should show table (based on backend logic)
    const shouldShowTable = (documentType) => {
        // Based on backend logic: IsInvoiceReceipt determines if table is shown
        // Documents that show tables: TaxInvoice, ReceiptTax, CreditNote, Receipt, PostponedReceipt
        return ['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(documentType);
    };


    // Helper function to check if document is deferred receipt
    const isDeferredReceipt = (documentType) => {
        return documentType === 'PostponedReceipt';
    };

    // Helper function to get the correct document type for backend
    const getBackendDocumentType = (documentType) => {
        // Backend only supports TaxInvoice and Receipt for document settings
        // PostponedReceipt should be treated as Receipt for settings but with DeferredReceipt flag
        if (documentType === 'PostponedReceipt') {
            return 'Receipt';
        }
        // For preview generation, we need to determine if it should be treated as TaxInvoice or Receipt
        // based on whether it shows a table (IsInvoiceReceipt)
        return shouldShowTable(documentType) ? 'TaxInvoice' : 'Receipt';
    };

    useEffect(() => {
        if (open) {
            loadDocumentSettings();
            loadCurrentSignatureAndProfile();
            loadClientItemsAndDefault();
        }
    }, [open, caseId, token]);

    const loadCurrentSignatureAndProfile = async () => {
        if (!client) {
            return;
        }

        try {
            // Reset modified flags
            setSignatureModified(false);
            setProfileModified(false);
            
            // Load current signature if exists
            // Backend now returns pre-signed URLs directly
            if (client.signature) {
                setSignaturePreview(client.signature);
            }

            // Load current profile picture if exists
            // Backend now returns pre-signed URLs directly
            if (client.profilePicture) {
                setProfilePreview(client.profilePicture);
            }
        } catch (error) {
            console.error('Error loading current signature and profile:', error);
        }
    };

    const loadClientItemsAndDefault = async () => {
        if (!caseId || !token) return;
        try {
            const [itemsRes, defaultRes] = await Promise.all([
                ClientItemsService.GetClientItemsByCaseId(caseId, token),
                ClientItemsService.GetDefaultClientItem(caseId, token),
            ]);
            if (itemsRes.ok) {
                const data = await itemsRes.json();
                setClientItems(Array.isArray(data) ? data : []);
            }
            if (defaultRes.ok) {
                const text = await defaultRes.text();
                const def = (text && text.trim()) ? JSON.parse(text) : null;
                setDefaultItem(def);
                setSelectedItemMakat(def?.makat ?? '');
            } else {
                setDefaultItem(null);
                setSelectedItemMakat('');
            }
        } catch (err) {
            console.error('Error loading client items / default item:', err);
            setClientItems([]);
            setDefaultItem(null);
            setSelectedItemMakat('');
        }
    };

    const handleSetDefaultItem = async () => {
        if (!caseId || !token) {
            toast.warning(t('SelectItemToSetAsDefault') || 'בחר פריט להגדרה כברירת מחדל');
            return;
        }
        setDefaultItemLoading(true);
        try {
            const makatToSet = (selectedItemMakat || '').trim();
            const res = await ClientItemsService.SetDefaultClientItem(caseId, makatToSet, token);
            if (res.ok) {
                toast.success(makatToSet ? (t('DefaultItemSetSuccessfully') || 'פריט ברירת המחדל עודכן') : (t('DefaultItemClearedSuccessfully') || 'בוטל פריט ברירת המחדל'));
                await loadClientItemsAndDefault();
            } else {
                const errText = await res.text();
                toast.error(t('ErrorSettingDefaultItem') || 'שגיאה בעדכון פריט ברירת מחדל');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorSettingDefaultItem') || 'שגיאה בעדכון פריט ברירת מחדל');
        }
        setDefaultItemLoading(false);
    };

    const handleSelectDefaultItemChange = (e) => {
        const value = e.target.value;
        if (value === '__add_client_item__') {
            setCreateItemModalOpen(true);
            return;
        }
        setSelectedItemMakat(value);
    };

    const handleSaveNewClientItem = async (itemsToAdd) => {
        if (!caseId || !token || !itemsToAdd?.length) return;
        setCreateItemLoading(true);
        try {
            const itemsWithImageUrls = await Promise.all(
                itemsToAdd.map(async (item) => {
                    let imageUrl = null;
                    if (item.imageFile) {
                        try {
                            const uploadResp = await ClientItemsService.UploadClientItemImage(
                                item.imageFile,
                                caseId,
                                item.makat,
                                token
                            );
                            if (uploadResp.ok) {
                                const uploadData = await uploadResp.json();
                                imageUrl = uploadData.imageUrl;
                            } else {
                                toast.warning(t('FailedToUploadImageForItem') + ` ${item.makat}`);
                            }
                        } catch (err) {
                            console.error('Image upload error:', err);
                        }
                    }
                    return {
                        CaseId: caseId,
                        Makat: item.makat,
                        Name: item.name,
                        Price: parseFloat(item.price) || 0,
                        IncludeVat: item.includeVat === true,
                        ImageUrl: imageUrl,
                    };
                })
            );
            const resp = await ClientItemsService.AddClientItems(itemsWithImageUrls, token);
            if (!resp.ok) {
                const errText = await resp.text();
                toast.error(t('FailedToAddClientItem') + `: ${errText}`);
            } else {
                toast.success(t('ClientItemAddedSuccessfully'));
                setCreateItemModalOpen(false);
                await loadClientItemsAndDefault();
                if (itemsToAdd.length > 0 && itemsToAdd[0].makat) {
                    setSelectedItemMakat(itemsToAdd[0].makat);
                }
            }
        } catch (err) {
            toast.error(t('ErrorAddingClientItem'));
            console.error(err);
        }
        setCreateItemLoading(false);
    };

    const loadDocumentSettings = async () => {
        try {
            setLoading(true);
            setError('');

            // Get settings for the case (no document type needed)
            const result = await GetDocumentSettings({
                caseId
            }, token);

            if (result) {
                // Ensure column settings have proper default values
                const columnSettings = result.columnSettings || {};
                const defaultColumnSettings = {
                    textSize: 11,
                    companyInfoTextSize: 16,
                    documentTitleTextSize: 20,
                    profilePictureShape: 'NoChooseShape',
                    boldDate: false,
                    width: 80,
                    height: 100,
                    backgroundColor: '#ffffff',
                    textColor: '#000000',
                    upperPanelBackgroundColor: '#fbfbfb',
                    svgColor: '#F9AA2A',
                    upperPanelColor: '#fbfbfb',
                    svgImageColor: '#F9AA2A',
                    logoPosition: 'left',
                    totalsLayout: 'horizontal',
                    documentFont: 'Montserrat'
                };

                // Merge with defaults, ensuring no null values
                const mergedColumnSettings = {
                    ...defaultColumnSettings,
                    ...columnSettings,
                    textSize: columnSettings.textSize ?? defaultColumnSettings.textSize,
                    companyInfoTextSize: columnSettings.companyInfoTextSize ?? defaultColumnSettings.companyInfoTextSize,
                    documentTitleTextSize: columnSettings.documentTitleTextSize ?? defaultColumnSettings.documentTitleTextSize,
                    profilePictureShape: columnSettings.profilePictureShape ?? defaultColumnSettings.profilePictureShape,
                    boldDate: columnSettings.boldDate ?? defaultColumnSettings.boldDate,
                    width: columnSettings.width ?? defaultColumnSettings.width,
                    height: columnSettings.height ?? defaultColumnSettings.height,
                    backgroundColor: columnSettings.backgroundColor ?? defaultColumnSettings.backgroundColor,
                    textColor: columnSettings.textColor ?? defaultColumnSettings.textColor,
                    upperPanelBackgroundColor: columnSettings.upperPanelBackgroundColor ?? defaultColumnSettings.upperPanelBackgroundColor,
                    svgColor: columnSettings.svgColor ?? defaultColumnSettings.svgColor,
                    upperPanelColor: columnSettings.upperPanelColor ?? defaultColumnSettings.upperPanelColor,
                    svgImageColor: columnSettings.svgImageColor ?? defaultColumnSettings.svgImageColor,
                    logoPosition: columnSettings.logoPosition ?? defaultColumnSettings.logoPosition,
                    totalsLayout: columnSettings.totalsLayout ?? defaultColumnSettings.totalsLayout,
                    documentFont: columnSettings.documentFont ?? defaultColumnSettings.documentFont
                };

                const newSettings = {
                    customDescription: result.constantClientDescription || '',
                    customFooter: result.constantInvoiceDescription || '',
                    headerRightText: result.headerRightText || '',
                    headerLeftText: result.headerLeftText || '',
                    itemsDisplayMode: result.itemsDisplayMode || '',
                    columnSettings: mergedColumnSettings
                };

                setSettings(newSettings);
            }
        } catch (err) {
            console.error('Error loading document settings:', err);
            setError(t('CheckDocumentSettings'));
        } finally {
            setLoading(false);
        }
    };
    // Simple function to generate PDF preview when needed (for save button)
    const generatePdfPreview = async () => {
        setPdfLoading(true);
        try {
            // This will be called only when user wants to see PDF preview or save
            // For now, we'll keep the existing PDF generation logic for the save functionality
            // Use default item for preview row when set, else sample item
            const previewItem = defaultItem && (defaultItem.makat || defaultItem.Makat)
                ? {
                    makat: defaultItem.makat ?? defaultItem.Makat ?? '001',
                    name: defaultItem.name ?? defaultItem.Name ?? 'מוצר/שירות לדוגמה',
                    quantity: '1',
                    price: String(parseFloat(defaultItem.price ?? defaultItem.Price ?? 0) || 1000),
                    discount: '0',
                    isNew: true
                }
                : {
                    makat: '001',
                    name: 'Sample Product/Service',
                    quantity: '1',
                    price: '1000',
                    discount: '0',
                    isNew: true
                };

            const documentData = {
                CaseId: caseId,
                AccountantId: accountantId,
                Name: client?.name || 'Sample Company',
                // Description: settings?.customDescription || (t('SampleDocumentForPreview') || 'מסמך דוגמא לתצוגה'),
                Reference: client?.clientCaseId ? `דוגמא-${client.clientCaseId}` : 'דוגמא-001',
                Date: new Date().toISOString(),
                DocumentDate: new Date().toISOString(),
                ClientName: client?.clientName || 'Sample Client',
                ClientCaseId: client?.clientCaseId || '123456789',
                PhoneNumber: client?.phoneNumber || '050-1234567',
                SecondaryPhoneNumber: client?.secondaryPhoneNumber || '',
                ClientPhoneNumber: client?.clientPhoneNumber || '050-9876543',
                Items: [previewItem],
                IsInvoiceReceipt: shouldShowTable(selectedDocumentType) && !isDeferredReceipt(selectedDocumentType),
                DeferredReceipt: isDeferredReceipt(selectedDocumentType),
                Title: DocumentTypesEnum.find(doc => doc.value === selectedDocumentType)?.label || 'Sample Document',
                Email: client?.email || '',
                Website: client?.website || '',
                Location: client?.location || '',
                ClientLocation: client?.clientLocation || '',
                ClientEmail: client?.clientEmail || '',
                PriceIncludingVAT: false,
                ForceNoTax: false,
                WithholdingTax: 0,
                Signature: signaturePreview,
                GeneratedBy: 'Preview',
                ForeignCurrencyCode: null,
                AddToInventory: false,
                reportingMonthDate: new Date().toISOString(),
                checks: [],
                bankTransactions: [],
                cash: [],
                creditCards: [],
                CustomDescription: settings.customDescription,
                CustomFooter: settings.customFooter,
                ColumnSettings: settings.columnSettings,
                type: ['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(selectedDocumentType) ? 'PaymentAcceptances' : selectedDocumentType.toLowerCase()
            };

            let controller, endpoint, type;

            if (['TaxInvoice', 'ReceiptTax', 'CreditNote', 'Receipt', 'PostponedReceipt'].includes(selectedDocumentType)) {
                controller = 'PaymentAcceptances';
                endpoint = 'GeneratePaymentAcceptanceDocumentDraftPdf';
                type = 'PaymentAcceptances';
            } else {
                controller = 'TransactionDocuments';
                endpoint = 'GenerateTransactionDocumentDraftPdf';
                type = selectedDocumentType.toLowerCase();
            }

            const pdfBlob = await DocumentService.CreateDraftInvoice(
                controller,
                endpoint,
                documentData,
                type,
                DocumentTypesEnum.find(doc => doc.value === selectedDocumentType)?.label || 'Sample Document',
                selectedDocumentType,
                token
            );

            if (pdfBlob) {
                const url = URL.createObjectURL(pdfBlob);
                setPdfPreview(url);
            } else {
                throw new Error('No PDF blob received from server');
            }
        } catch (err) {
            console.error('Error generating PDF preview:', err);
            toast.error(`Error generating PDF preview: ${err.message}`);
            // Fallback to HTML preview if PDF generation fails
            setPreviewMode('html');
        } finally {
            setPdfLoading(false);
        }
    };





    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setSignaturePreview(objectUrl);
            setNewSignatureFile(file);
            setSignatureModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleProfilePictureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setProfilePreview(objectUrl);
            setNewProfileFile(file);
            setProfileModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleSignatureChange = () => {
        if (sigPad.current) {
            const signatureDataUrl = sigPad.current.toDataURL();
            setSignaturePreview(signatureDataUrl);
            setNewSignatureFile(null);
            setSignatureModified(true);
            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }
        }
    };

    const handleClearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
        }
        setSignaturePreview('');
        setNewSignatureFile(null);
        setTextSignature('');
        setSignatureModified(true);
        // Force HTML preview mode for live preview
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleRemoveSignature = async () => {
        if (!signaturePreview) return;
        const result = await Swal.fire({
            title: t('confirmDeleteTitle') || 'Confirm Remove',
            text: t('AreYouSureRemoveSignature') || 'Are you sure you want to remove the signature? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete') || 'Yes, Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true
        });
        if (!result.isConfirmed) return;
        try {
            const response = await ClientService.ClearSignature(token, caseId);
            if (response.ok) {
                setSignaturePreview('');
                setNewSignatureFile(null);
                setSignatureModified(false);
                toast.success(t('SignatureClearedSuccessfully') || 'Signature cleared successfully');
                // Update document settings entity and refresh client data (same as handleSave)
                const currentSettings = settings;
                await UpdateDocumentSettings({
                    caseId,
                    accountantId,
                    constantClientDescription: currentSettings.customDescription || null,
                    constantInvoiceDescription: currentSettings.customFooter || null,
                    headerRightText: currentSettings.headerRightText || null,
                    headerLeftText: currentSettings.headerLeftText || null,
                    itemsDisplayMode: currentSettings.itemsDisplayMode || null,
                    columnSettings: currentSettings.columnSettings
                }, token);
                if (onSave) {
                    await onSave();
                }
            } else {
                toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
        }
    };

    const handleRemoveProfilePicture = async () => {
        if (!profilePreview) return;
        const result = await Swal.fire({
            title: t('confirmDeleteTitle') || 'Confirm Remove',
            text: t('AreYouSureRemoveProfilePicture') || 'Are you sure you want to remove the profile picture? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('YesDelete') || 'Yes, Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: true
        });
        if (!result.isConfirmed) return;
        try {
            const response = await ClientService.ClearProfilePhoto(token, caseId);
            if (response.ok) {
                setProfilePreview('');
                setNewProfileFile(null);
                setProfileModified(false);
                toast.success(t('ProfilePhotoClearedSuccessfully') || 'Profile photo cleared successfully');
                // Update document settings entity and refresh client data (same as handleSave)
                const currentSettings = settings;
                await UpdateDocumentSettings({
                    caseId,
                    accountantId,
                    constantClientDescription: currentSettings.customDescription || null,
                    constantInvoiceDescription: currentSettings.customFooter || null,
                    headerRightText: currentSettings.headerRightText || null,
                    headerLeftText: currentSettings.headerLeftText || null,
                    itemsDisplayMode: currentSettings.itemsDisplayMode || null,
                    columnSettings: currentSettings.columnSettings
                }, token);
                if (onSave) {
                    await onSave();
                }
            } else {
                toast.error(t('ErrorClearingProfilePhoto') || 'Error clearing profile photo');
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorClearingProfilePhoto') || 'Error clearing profile photo');
        }
    };

    const generateTextSignatureImage = async () => {
        if (!textSignature.trim()) {
            toast.error(t('PleaseEnterTextForSignature') || 'Please enter text for signature');
            return;
        }

        const hiddenDiv = document.createElement('div');
        hiddenDiv.innerHTML = textSignature;
        hiddenDiv.style.position = 'absolute';
        hiddenDiv.style.left = '-9999px';
        hiddenDiv.style.top = '-9999px';
        hiddenDiv.style.width = '700px';
        hiddenDiv.style.height = '200px';
        hiddenDiv.style.fontSize = '40px';
        hiddenDiv.style.color = '#000';
        hiddenDiv.style.textAlign = 'center';

        const fontMatch = textSignature.match(/font-family:([^;"]+)/);
        const appliedFont = fontMatch ? fontMatch[1].trim() : selectedFont;
        hiddenDiv.style.fontFamily = appliedFont;

        document.body.appendChild(hiddenDiv);

        try {
            const canvas = await html2canvas(hiddenDiv, { backgroundColor: null });
            const dataUrl = canvas.toDataURL('image/png');
            setSignaturePreview(dataUrl);
            setNewSignatureFile(null);
            setSignatureModified(true);

            // Force HTML preview mode for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }

            toast.success(t('TextSignatureGenerated') || 'Text signature generated successfully');
        } catch (error) {
            console.error('Error rendering text to image:', error);
            toast.error(t('ErrorGeneratingTextSignature') || 'Error generating text signature');
        } finally {
            document.body.removeChild(hiddenDiv);
        }
    };



    const handleSave = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            // Handle signature changes (upload, clear, or no change)
            if (signatureModified) {
                // Check if signature was cleared (empty string and no file)
                if (signaturePreview === '' && !newSignatureFile) {
                    // Clear the signature on the backend
                    const response = await ClientService.ClearSignature(token, caseId);
                    if (response.ok) {
                        // Reset modification flags
                        setSignatureModified(false);
                        setNewSignatureFile(null);
                        setSignaturePreview('');
                        toast.success(t('SignatureClearedSuccessfully') || 'Signature cleared successfully');
                    } else {
                        toast.error(t('ErrorClearingSignature') || 'Error clearing signature');
                    }
                } else if (newSignatureFile || (signaturePreview && signaturePreview !== '')) {
                    // Upload new signature (either file upload, drawn signature, or text signature)
                    let fileToUpload;

                    if (newSignatureFile) {
                        // Handle uploaded file
                        if (typeof newSignatureFile === 'string' && newSignatureFile.startsWith('data:image/')) {
                            const base64Data = newSignatureFile.split(',')[1];
                            const binaryData = atob(base64Data);
                            const arrayBuffer = new Uint8Array(binaryData.length);
                            for (let i = 0; i < binaryData.length; i++) {
                                arrayBuffer[i] = binaryData.charCodeAt(i);
                            }
                            const blob = new Blob([arrayBuffer], { type: 'image/png' });
                            fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                        } else {
                            fileToUpload = newSignatureFile;
                        }
                    } else if (signaturePreview && signaturePreview.startsWith('data:image/')) {
                        // Handle drawn signature or text signature (data URL)
                        const base64Data = signaturePreview.split(',')[1];
                        const binaryData = atob(base64Data);
                        const arrayBuffer = new Uint8Array(binaryData.length);
                        for (let i = 0; i < binaryData.length; i++) {
                            arrayBuffer[i] = binaryData.charCodeAt(i);
                        }
                        const blob = new Blob([arrayBuffer], { type: 'image/png' });
                        fileToUpload = new File([blob], 'signature.png', { type: 'image/png' });
                    }

                    if (fileToUpload) {
                        const response = await ClientService.UploadSignature(token, accountantId, caseId, fileToUpload);
                        if (response.ok) {
                            // Parse response to get the pre-signed URL
                            const data = await response.json();
                            if (data.url) {
                                // Update preview with the new URL from server
                                setSignaturePreview(data.url);
                            }
                            // Reset modification flags
                            setSignatureModified(false);
                            setNewSignatureFile(null);
                            toast.success(t('SignatureUploadedSuccessfully'));
                        } else {
                            toast.error(t('ErrorUploadingSignatureFiles'));
                        }
                    }
                }
            }

            // Upload profile picture if there's a new one
            if (profileModified && (newProfileFile || (profilePreview && profilePreview !== ''))) {
                let fileToUpload = newProfileFile;

                if (!fileToUpload && profilePreview && profilePreview.startsWith('data:image/')) {
                    // Handle profile picture that might be a data URL
                    const base64Data = profilePreview.split(',')[1];
                    const binaryData = atob(base64Data);
                    const arrayBuffer = new Uint8Array(binaryData.length);
                    for (let i = 0; i < binaryData.length; i++) {
                        arrayBuffer[i] = binaryData.charCodeAt(i);
                    }
                    const blob = new Blob([arrayBuffer], { type: 'image/png' });
                    fileToUpload = new File([blob], 'profile.png', { type: 'image/png' });
                }

                if (fileToUpload) {
                    const response = await ClientService.UploadProfilePhoto(
                        token,
                        accountantId,
                        caseId,
                        fileToUpload
                    );
                    if (response.ok) {
                        // Parse response to get the pre-signed URL
                        const data = await response.json();
                        if (data.url) {
                            // Update preview with the new URL from server
                            setProfilePreview(data.url);
                        }
                        // Reset modification flags
                        setProfileModified(false);
                        setNewProfileFile(null);
                        toast.success(t('ProfilePhotoUploadedSuccessfully'));
                    } else {
                        toast.error(t('ErrorUploadingProfileFiles'));
                    }
                } else {
                    console.log('No profile file to upload');
                }
            }

            const currentSettings = settings;

            await UpdateDocumentSettings({
                caseId,
                accountantId,
                constantClientDescription: currentSettings.customDescription || null,
                constantInvoiceDescription: currentSettings.customFooter || null,
                headerRightText: currentSettings.headerRightText || null,
                headerLeftText: currentSettings.headerLeftText || null,
                itemsDisplayMode: currentSettings.itemsDisplayMode || null,
                columnSettings: currentSettings.columnSettings
            }, token);

            setSuccess(t('DocumentSettingsSavedSuccessfully'));

            // Call parent's onSave callback to refresh client data
            if (onSave) {
                await onSave();
            }


        } catch (err) {
            console.error('Error saving document settings:', err);
            setError(t('ErrorSavingDocumentSettings'));
        } finally {
            setLoading(false);
        }
    };

    const handleSettingChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));

        // Force HTML preview mode when making changes for live preview
        if (previewMode === 'pdf') {
            setPreviewMode('html');
        }
    };

    const handleColumnSettingChange = (field, value) => {
        setSettings(prev => {
            const newSettings = {
                ...prev,
                columnSettings: {
                    ...prev.columnSettings,
                    [field]: value
                }
            };

            // Force HTML preview mode when making changes for live preview
            if (previewMode === 'pdf') {
                setPreviewMode('html');
            }

            return newSettings;
        });
    };



    const currentSettings = settings;

    const renderSignatureSection = () => (
        <StyledAccordion defaultExpanded dir={direction}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} dir={direction}>
                <CreateIcon sx={{ mr: 1, color: '#4285f4', fontSize: 24 }} />
                <Typography variant="h6" sx={{
                    color: '#202124',
                    fontSize: '18px',
                    fontWeight: 700
                }} dir={direction}>{t('SignatureAndProfilePicture')}</Typography>
            </AccordionSummary>
            <AccordionDetails dir={direction}>
                <Grid container spacing={2} dir={direction}>
                    {/* Signature Section */}
                    <Grid item xs={12} md={6} dir={direction}>
                        <Box sx={{
                            p: 1,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e8eaed'
                        }} dir={direction}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }} dir={direction}>
                                <Typography variant="h6" sx={{
                                    color: '#202124',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }} dir={direction}>
                                    {t('Signature')}
                                </Typography>
                                {signaturePreview && (
                                    <Tooltip title={t('RemoveSignature') || 'Remove signature'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveSignature}
                                            sx={{
                                                color: '#d33',
                                                backgroundColor: '#EBEBEB',
                                                '&:hover': {
                                                    backgroundColor: '#D0D0D0'
                                                }
                                            }}
                                            aria-label="remove signature"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {signaturePreview && (
                                <Box sx={{ mb: 2, textAlign: 'center' }} dir={direction}>
                                    <img
                                        src={signaturePreview}
                                        alt="signature-preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '80px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Box>
                            )}

                            {addSignaturePad && (
                                <Box sx={{ mb: 1 }} dir={direction}>
                                    <SignaturePad
                                        ref={sigPad}
                                        canvasProps={{
                                            style: {
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                width: '100%',
                                                height: '120px',
                                                backgroundColor: '#ffffff'
                                            },
                                        }}
                                        onEnd={handleSignatureChange}
                                    />
                                    <StyledButton
                                        variant="outlined"
                                        onClick={handleClearSignature}
                                        sx={{ mt: 2 }}
                                        dir={direction}
                                        size="small"
                                    >
                                        {t('ClearSignature')}
                                    </StyledButton>
                                </Box>
                            )}

                            <Stack direction="row" spacing={0.8} sx={{ dir: direction, justifyContent: 'center' }}>
                                <StyledButton
                                    variant="contained"
                                    component="label"
                                    size="small"
                                    startIcon={<UploadIcon />}
                                    dir={direction}
                                >
                                    {t('UploadSignature')}
                                    <input
                                        type="file"
                                        hidden
                                        onChange={handleSignatureUpload}
                                    />
                                </StyledButton>
                                <StyledButton
                                    variant="contained"
                                    size="small"
                                    startIcon={<DrawIcon />}
                                    onClick={() => setAddSignaturePad(!addSignaturePad)}
                                    dir={direction}
                                >
                                    {t('DrawSignature')}
                                </StyledButton>
                                <StyledButton
                                    variant="contained"
                                    size="small"
                                    startIcon={<TextIncreaseIcon />}
                                    onClick={() => setTextSignatureOption(!textSignatureOption)}
                                    dir={direction}
                                >
                                    {t('TextSignature')}
                                </StyledButton>
                            </Stack>

                            {/* Signature Size Slider */}
                            <Box sx={{ mt: 2 }} dir={direction}>
                                <Divider sx={{ my: 1 }}>
                                    <Chip label={t('SignatureSize') || 'גודל חתימה'} size="small" sx={{ fontSize: '12px' }} />
                                </Divider>
                                <Slider
                                    value={currentSettings.columnSettings.signatureWidth || 80}
                                    onChange={(e, newValue) => {
                                        handleColumnSettingChange('signatureWidth', newValue);
                                        handleColumnSettingChange('signatureHeight', Math.round(newValue * 0.5));
                                    }}
                                    min={40}
                                    max={150}
                                    step={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    sx={{
                                        '& .MuiSlider-track': {
                                            backgroundColor: '#1976d2',
                                        },
                                        '& .MuiSlider-thumb': {
                                            backgroundColor: '#1976d2',
                                        },
                                        '& .MuiSlider-mark': {
                                            backgroundColor: '#1976d2',
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{
                                    color: '#666666',
                                    fontSize: '12px'
                                }}>
                                    {currentSettings.columnSettings.signatureWidth || 80}mm × {currentSettings.columnSettings.signatureHeight || 40}mm
                                </Typography>
                            </Box>

                            {textSignatureOption && (
                                <Box sx={{ mt: 2 }} dir={direction}>
                                    <TextField
                                        select
                                        label={t('Font')}
                                        value={selectedFont}
                                        onChange={(e) => setSelectedFont(e.target.value)}
                                        fullWidth
                                        size="small"
                                        sx={{ mb: 1 }}
                                        dir={direction}
                                    >
                                        {fonts.map((font) => (
                                            <MenuItem key={font} value={font} style={{ fontFamily: font }} dir={direction}>
                                                {font}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <ReactQuill
                                        theme="snow"
                                        value={textSignature}
                                        onChange={setTextSignature}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                [{ 'align': [] }],
                                            ],
                                        }}
                                        style={{ height: '80px', marginBottom: '12px' }}
                                    />
                                    <StyledButton
                                        variant="contained"
                                        onClick={generateTextSignatureImage}
                                        fullWidth
                                        dir={direction}
                                        size="medium"
                                    >
                                        {t('GenerateTextSignature')}
                                    </StyledButton>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Profile Picture Section */}
                    <Grid item xs={12} md={6} dir={direction}>
                        <Box sx={{
                            p: 1,
                            backgroundColor: '#f8f9fa',
                            borderRadius: '12px',
                            border: '1px solid #e8eaed'
                        }} dir={direction}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }} dir={direction}>
                                <Typography variant="h6" sx={{
                                    color: '#202124',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }} dir={direction}>
                                    {t('ProfilePicture')}
                                </Typography>
                                {profilePreview && (
                                    <Tooltip title={t('RemoveProfilePicture') || 'Remove profile picture'}>
                                        <IconButton
                                            size="small"
                                            onClick={handleRemoveProfilePicture}
                                            sx={{
                                                color: '#d33',
                                                backgroundColor: '#EBEBEB',
                                                '&:hover': {
                                                    backgroundColor: '#D0D0D0'
                                                }
                                            }}
                                            aria-label="remove profile picture"
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {profilePreview && (
                                <Box sx={{ mb: 2, textAlign: 'center' }} dir={direction}>
                                    <img
                                        src={profilePreview}
                                        alt="profile-preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '80px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <StyledButton
                                variant="contained"
                                component="label"
                                fullWidth
                                startIcon={<UploadIcon />}
                                dir={direction}
                                size="medium"
                                sx={{width: '80%'}}
                            >
                                {t('UploadProfilePicture')}
                                <input
                                    type="file"
                                    hidden
                                    onChange={handleProfilePictureUpload}
                                />
                            </StyledButton>
                            </Box>

                            {/* Profile Picture Size Slider */}
                            <Box sx={{ mt: 2 }} dir={direction}>
                                <Divider sx={{ my: 1 }}>
                                    <Chip label={t('ProfilePictureSize') || 'גודל תמונת פרופיל'} size="small" sx={{ fontSize: '12px' }} />
                                </Divider>
                                <Slider
                                    value={currentSettings.columnSettings.width || 80}
                                    onChange={(e, newValue) => {
                                        // Set both width and height to the same value for relative sizing
                                        handleColumnSettingChange('width', newValue);
                                        handleColumnSettingChange('height', newValue);
                                    }}
                                    min={40}
                                    max={120}
                                    step={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    sx={{
                                        '& .MuiSlider-track': {
                                            backgroundColor: '#1976d2',
                                        },
                                        '& .MuiSlider-thumb': {
                                            backgroundColor: '#1976d2',
                                        },
                                        '& .MuiSlider-mark': {
                                            backgroundColor: '#1976d2',
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{
                                    color: '#666666',
                                    fontSize: '12px'
                                }}>
                                    {currentSettings.columnSettings.width || 80}mm (width & height)
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </StyledAccordion>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                sx={{
                    direction,
                    '& .MuiDialog-paper': {
                        borderRadius: '20px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                        overflow: 'hidden',
                        border: '1px solid #e8eaed'
                    }
                }}
                dir={direction}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid #e8eaed'
                }} dir={direction}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }} dir={direction}>
                        <SettingsIcon sx={{ mr: 1, color: '#4285f4', fontSize: 22 }} />
                        <Typography variant="h6" dir={direction} sx={{
                            fontWeight: 700,
                            color: '#202124',
                            fontSize: '20px'
                        }}>{t('DocumentSettings')}</Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        dir={direction}
                        sx={{
                            color: '#5f6368',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            '&:hover': {
                                backgroundColor: '#f1f3f4',
                                color: '#202124'
                            }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, backgroundColor: '#fafbfc' }} dir={direction}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, m: 2 }} dir={direction}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2, m: 2 }} dir={direction}>
                            {success}
                        </Alert>
                    )}

                    {/* Main Content - Two Column Layout */}
                    <Box sx={{ display: 'flex', height: '75vh', gap: 0 }} dir={direction}>
                        {/* Left Panel - Customization Options */}
                        <Box sx={{
                            width: '50%',
                            p: 2,
                            borderRight: '1px solid #e8eaed',
                            backgroundColor: '#ffffff'
                        }} >




                            {renderSignatureSection()}

                            {/* Default item for documents */}
                            <StyledAccordion sx={{ mt: 1 }} dir={direction}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} dir={direction}>
                                    <BusinessCenterIcon sx={{ mr: 1, color: '#4285f4', fontSize: 24 }} />
                                    <Typography variant="h6" sx={{
                                        color: '#202124',
                                        fontSize: '18px',
                                        fontWeight: 700
                                    }} dir={direction}>{t('DefaultItemForDocuments') || 'פריט ברירת מחדל למסמכים'}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1.5 }} dir={direction}>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#5f6368' }} dir={direction}>
                                        {t('DefaultItemDescription') || 'בחר פריט שיופיע אוטומטית בטבלת הפריטים בעת יצירת חשבונית/קבלה.'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }} dir={direction}>
                                        <Autocomplete
                                            size="small"
                                            dir={direction}
                                            sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 260 }}
                                            options={[
                                                { makat: '', name: t('NoneText') || 'ללא', __none: true },
                                                { makat: '__add_client_item__', name: `+ ${t('AddClientItem')}`, __add: true },
                                                ...(clientItems || [])
                                            ]}
                                            value={!selectedItemMakat
                                                ? { makat: '', name: t('NoneText') || 'ללא', __none: true }
                                                : (clientItems || []).find((i) => (i.makat || '') === selectedItemMakat) || null}
                                            isOptionEqualToValue={(option, value) => {
                                                if (!value) return false;
                                                if (option.__none && value.__none) return true;
                                                if (option.__add && value.__add) return true;
                                                return (option.makat || '') === (value.makat || '');
                                            }}
                                            onChange={(e, newValue) => {
                                                if (!newValue) {
                                                    setSelectedItemMakat('');
                                                    return;
                                                }
                                                if (newValue.__add) {
                                                    setCreateItemModalOpen(true);
                                                    return;
                                                }
                                                if (newValue.__none) {
                                                    setSelectedItemMakat('');
                                                    return;
                                                }
                                                setSelectedItemMakat(newValue.makat || '');
                                            }}
                                            getOptionLabel={(opt) => (opt && opt.name) ? (opt.makat && !opt.__none && !opt.__add ? `${opt.makat} – ${opt.name}` : opt.name) : ''}
                                            filterOptions={(options, { inputValue }) => {
                                                const noneAndAdd = options.filter((o) => o.__none || o.__add);
                                                const rest = options.filter((o) => !o.__none && !o.__add);
                                                const term = (inputValue || '').toLowerCase().trim();
                                                if (!term) return options;
                                                const filtered = rest.filter(
                                                    (o) => (o.makat || '').toLowerCase().includes(term) || (o.name || '').toLowerCase().includes(term)
                                                );
                                                return [...noneAndAdd, ...filtered];
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('SelectItem') || 'בחר פריט'}
                                                    dir={direction}
                                                    sx={{ '& .MuiInputBase-root': { fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } }}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <li {...props} key={option.__add ? '__add' : option.__none ? '__none' : (option.id || option.makat || '')} dir={direction}>
                                                    {option.__add ? (
                                                        <span style={{ fontWeight: 600, color: '#4285f4' }}>{option.name}</span>
                                                    ) : option.__none ? (
                                                        option.name
                                                    ) : (
                                                        <>
                                                            {option.makat} – {option.name}
                                                            {defaultItem?.makat === option.makat ? ` (${t('Default') || 'ברירת מחדל'})` : ''}
                                                        </>
                                                    )}
                                                </li>
                                            )}
                                        />
                                        <StyledButton
                                            variant="contained"
                                            size="small"
                                            onClick={handleSetDefaultItem}
                                            disabled={defaultItemLoading}
                                            dir={direction}
                                        >
                                            {defaultItemLoading ? t('Saving') : (t('SetAsDefault') || 'הגדר כברירת מחדל')}
                                        </StyledButton>
                                    </Box>
                                </AccordionDetails>
                            </StyledAccordion>

                            {/* Column Settings */}
                            <StyledAccordion sx={{ mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} dir={direction}>
                                    <ViewColumnIcon sx={{ mr: 1, color: '#4285f4', fontSize: 24 }} />
                                    <Typography variant="h6" sx={{
                                        color: '#202124',
                                        fontSize: '18px',
                                        fontWeight: 700
                                    }} dir={direction}>{t('ColumnSettings')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} >
                                            <Divider>
                                                <Chip label={t('TextSizes') || 'גדלי טקסט'} size="small" />
                                            </Divider>
                                        </Grid>

                                        {/* General Text Size Setting */}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} >
                                                {t('TextSize')}
                                            </Typography>
                                            <Slider
                                                value={currentSettings.columnSettings.textSize}
                                                onChange={(e, newValue) => handleColumnSettingChange('textSize', newValue)}
                                                min={8}
                                                max={16}
                                                step={1}
                                                marks
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    '& .MuiSlider-track': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-thumb': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-mark': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: '#666666',
                                                fontSize: '12px'
                                            }}>
                                                {currentSettings.columnSettings.textSize}px
                                            </Typography>
                                        </Grid>

                                        {/* Company Info Text Size Setting */}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} >
                                                {t('CompanyInfoTextSize')}
                                            </Typography>
                                            <Slider
                                                value={currentSettings.columnSettings.companyInfoTextSize}
                                                onChange={(e, newValue) => handleColumnSettingChange('companyInfoTextSize', newValue)}
                                                min={12}
                                                max={24}
                                                step={1}
                                                marks
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    '& .MuiSlider-track': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-thumb': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-mark': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: '#666666',
                                                fontSize: '12px'
                                            }}>
                                                {currentSettings.columnSettings.companyInfoTextSize}px
                                            </Typography>
                                        </Grid>

                                        {/* Document Title Text Size Setting */}
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} >
                                                {t('DocumentTitleTextSize')}
                                            </Typography>
                                            <Slider
                                                value={currentSettings.columnSettings.documentTitleTextSize}
                                                onChange={(e, newValue) => handleColumnSettingChange('documentTitleTextSize', newValue)}
                                                min={16}
                                                max={32}
                                                step={1}
                                                marks
                                                valueLabelDisplay="auto"
                                                sx={{
                                                    '& .MuiSlider-track': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-thumb': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                    '& .MuiSlider-mark': {
                                                        backgroundColor: '#1976d2',
                                                    },
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: '#666666',
                                                fontSize: '12px'
                                            }}>
                                                {currentSettings.columnSettings.documentTitleTextSize}px
                                            </Typography>
                                        </Grid>
                                        {/* Bold Date */}
                                        <Grid item xs={6} dir={direction} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={currentSettings.columnSettings.boldDate}
                                                        onChange={(e) => handleColumnSettingChange('boldDate', e.target.checked)}
                                                        dir={direction}
                                                        sx={{
                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                color: '#4285f4',
                                                            },
                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                backgroundColor: '#4285f4',
                                                            },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Typography sx={{
                                                        fontSize: '15px',
                                                        color: '#202124',
                                                        fontWeight: 500
                                                    }} dir={direction}>
                                                        {t('BoldDate')}
                                                    </Typography>
                                                }
                                                dir={direction}
                                            />
                                                                                    {/* Document font */}
                                        <Grid item xs={6} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('DocumentFont') || t('Font') || 'Document font'}
                                            </Typography>
                                            <Select
                                                dir={direction}
                                                value={currentSettings.columnSettings.documentFont || 'Montserrat'}
                                                onChange={(e) => handleColumnSettingChange('documentFont', e.target.value)}
                                                fullWidth
                                                size="small"
                                            >
                                                {documentFontOptions.map((font) => (
                                                    <MenuItem key={font} value={font} dir={direction} sx={{ fontFamily: font, textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                        {font}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </Grid>
                                        </Grid>



                                        {/* Logo Position */}
                                        <Grid item xs={3}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('LogoPosition')}
                                            </Typography>
                                            <Select
                                                dir={direction}
                                                value={currentSettings.columnSettings.logoPosition || 'left'}
                                                onChange={(e) => handleColumnSettingChange('logoPosition', e.target.value)}
                                                fullWidth
                                                size="small"
                                            >
                                                <MenuItem value="center" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Center')}</MenuItem>
                                                <MenuItem value="left" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Left')}</MenuItem>
                                            </Select>
                                        </Grid> 
                                        
                                        {/* Date Alignment */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('DateAlignment') || 'יישור תאריך'}
                                            </Typography>
                                            <Select
                                                dir={direction}
                                                value={currentSettings.columnSettings.dateAlign || 'right'}
                                                onChange={(e) => handleColumnSettingChange('dateAlign', e.target.value)}
                                                fullWidth
                                                size="small"
                                            >
                                                <MenuItem value="right" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('AlignRight') || 'יישור לימין'}</MenuItem>
                                                <MenuItem value="left" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('AlignLeft') || 'יישור לשמאל'}</MenuItem>
                                            </Select>
                                        </Grid>
                                        {/* Totals Layout */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('TotalsLayout')}
                                            </Typography>
                                            <Select
                                                dir={direction}
                                                value={currentSettings.columnSettings.totalsLayout || 'horizontal'}
                                                onChange={(e) => handleColumnSettingChange('totalsLayout', e.target.value)}
                                                fullWidth
                                                size="small"
                                            >
                                                <MenuItem value="horizontal" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Horizontal')}</MenuItem>
                                                <MenuItem value="vertical" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Vertical')}</MenuItem>
                                            </Select>
                                        </Grid>
                                        {/* Profile Picture Shape */}
                                        <Grid item xs={3}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('ProfilePictureShape')}
                                            </Typography>
                                            <Select
                                                dir={direction}
                                                value={currentSettings.columnSettings.profilePictureShape}
                                                onChange={(e) => handleColumnSettingChange('profilePictureShape', e.target.value)}
                                                fullWidth
                                                size="small"

                                            >
                                                <MenuItem value="NoChooseShape" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('NoChooseShape')}</MenuItem>
                                                <MenuItem value="circle" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Circle')}</MenuItem>
                                                <MenuItem value="rectangle" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Rectangle')}</MenuItem>
                                                <MenuItem value="hexagon" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Hexagon')}</MenuItem>
                                                <MenuItem value="diamond" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Diamond')}</MenuItem>
                                                <MenuItem value="roundedRectangle" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('RoundedRectangle')}</MenuItem>
                                                <MenuItem value="oval" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Oval')}</MenuItem>
                                                <MenuItem value="triangle" dir={direction} sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>{t('Triangle')}</MenuItem>
                                            </Select>
                                        </Grid>

                                        <Grid item xs={12} sx={{ mt: 1}}>
                                            <Divider>
                                                <Chip label={t('Colors') || 'צבעים'} size="small" />
                                            </Divider>
                                        </Grid>

                                        {/* Title Text Color */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('TitleTextColor') || 'צבע טקסט כותרות'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <input
                                                    type="color"
                                                    value={currentSettings.columnSettings.titleTextColor || '#000000'}
                                                    onChange={(e) => handleColumnSettingChange('titleTextColor', e.target.value)}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <TextField
                                                    value={currentSettings.columnSettings.titleTextColor || '#000000'}
                                                    onChange={(e) => handleColumnSettingChange('titleTextColor', e.target.value)}
                                                    size="small"
                                                    sx={{ flex: 1 }}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Text Color */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('TextColor')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <input
                                                    type="color"
                                                    value={currentSettings.columnSettings.textColor}
                                                    onChange={(e) => handleColumnSettingChange('textColor', e.target.value)}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <TextField
                                                    value={currentSettings.columnSettings.textColor}
                                                    onChange={(e) => handleColumnSettingChange('textColor', e.target.value)}
                                                    size="small"
                                                    sx={{ flex: 1 }}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Background Color */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('BackgroundColor')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <input
                                                    type="color"
                                                    value={currentSettings.columnSettings.backgroundColor}
                                                    onChange={(e) => handleColumnSettingChange('backgroundColor', e.target.value)}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <TextField
                                                    value={currentSettings.columnSettings.backgroundColor}
                                                    onChange={(e) => handleColumnSettingChange('backgroundColor', e.target.value)}
                                                    size="small"
                                                    sx={{ flex: 1 }}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Upper Panel Color */}
                                        <Grid item xs={3} dir={direction}>
                                            <Typography variant="subtitle2" sx={{
                                                color: '#333333',
                                                fontSize: '14px',
                                                fontWeight: 500
                                            }} gutterBottom>
                                                {t('DocumentHeader')}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <input
                                                    type="color"
                                                    value={currentSettings.columnSettings.upperPanelColor}
                                                    onChange={(e) => handleColumnSettingChange('upperPanelColor', e.target.value)}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer'
                                                    }}
                                                />
                                                <TextField
                                                    value={currentSettings.columnSettings.upperPanelColor}
                                                    onChange={(e) => handleColumnSettingChange('upperPanelColor', e.target.value)}
                                                    size="small"
                                                    sx={{ flex: 1 }}
                                                />
                                            </Box>
                                        </Grid>

                                    </Grid>
                                </AccordionDetails>
                            </StyledAccordion>

                            {/* Description and Footer */}
                            <StyledAccordion sx={{ mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} dir={direction}>
                                    <DescriptionIcon sx={{ mr: 1, color: '#4285f4', fontSize: 20 }} />
                                    <Typography variant="h6" sx={{
                                        color: '#202124',
                                        fontSize: '16px',
                                        fontWeight: 600
                                    }} dir={direction}>{t('DescriptionAndFooter')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails dir={direction}>
                                    <Grid container spacing={1} dir={direction}>
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <TextField
                                                label={t('EnterCustomDescription')}
                                                multiline
                                                rows={3}
                                                value={currentSettings.customDescription}
                                                onChange={(e) => handleSettingChange('customDescription', e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                dir={direction}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <TextField
                                                label={t('EnterCustomFooter')}
                                                multiline
                                                rows={3}
                                                value={currentSettings.customFooter}
                                                onChange={(e) => handleSettingChange('customFooter', e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                dir={direction}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <TextField
                                                label={t('HeaderRightText') || 'טקסט כותרת ימין'}
                                                multiline
                                                rows={2}
                                                value={currentSettings.headerRightText || ''}
                                                onChange={(e) => handleSettingChange('headerRightText', e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                dir={direction}
                                                placeholder={t('HeaderRightTextPlaceholder') || 'הזן טקסט לכותרת בצד ימין'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} dir={direction}>
                                            <TextField
                                                label={t('HeaderLeftText') || 'טקסט כותרת שמאל'}
                                                multiline
                                                rows={2}
                                                value={currentSettings.headerLeftText || ''}
                                                onChange={(e) => handleSettingChange('headerLeftText', e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                dir={direction}
                                                placeholder={t('HeaderLeftTextPlaceholder') || 'הזן טקסט לכותרת בצד שמאל'}
                                            />
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </StyledAccordion>

                            {/* Business Services Settings */}
                            <StyledAccordion sx={{ mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} >
                                    <BusinessCenterIcon sx={{ mr: 1, color: '#4285f4', fontSize: 24 }} />
                                    <Typography variant="h6" sx={{
                                        color: '#202124',
                                        fontSize: '18px',
                                        fontWeight: 700
                                    }} >{t('BusinessServicesSettings')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails dir={direction}>
                                    <Grid container spacing={1} dir={direction}>
                                        <Grid item xs={12} dir={direction}>
                                            <FormControl fullWidth size="small" dir={direction}>
                                                <InputLabel dir={direction}>{t('ItemsDisplayMode')}</InputLabel>
                                                <Select
                                                    value={currentSettings.itemsDisplayMode || 'normal'}
                                                    onChange={(e) => handleSettingChange('itemsDisplayMode', e.target.value)}
                                                    label={t('ItemsDisplayMode')}
                                                    dir={direction}
                                                >
                                                    <MenuItem value="normal" dir={direction}>{t('NormalDisplay')}</MenuItem>
                                                    <MenuItem value="descriptionOnly" dir={direction}>{t('DescriptionOnly')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </AccordionDetails>
                            </StyledAccordion>
                        </Box>

                        {/* Right Panel - HTML Preview */}
                        <Box sx={{
                            width: '50%',
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: '#ffffff'
                        }} dir={direction}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '10px',
                                borderBottom: '1px solid #e8eaed'
                            }} dir={direction}>

                                <Typography variant="h6" sx={{
                                    color: '#202124',
                                    fontSize: '20px',
                                    fontWeight: 700
                                }} dir={direction}>
                                    {t('LivePreview') || 'Live Preview'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }} dir={direction}>

                                    <StyledButton
                                        variant={previewMode === 'html' ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => setPreviewMode('html')}
                                        dir={direction}
                                    >
                                        {t('LivePreview')}
                                    </StyledButton>
                                    <StyledButton
                                        variant={previewMode === 'pdf' ? 'contained' : 'outlined'}
                                        size="small"
                                        startIcon={<Refresh />}
                                        onClick={() => {
                                            setPreviewMode('pdf');
                                            generatePdfPreview();
                                        }}
                                        disabled={pdfLoading}
                                        dir={direction}
                                    >
                                        {t('PDFPreview')}
                                    </StyledButton>
                                    <Box
                                        variant="contained"
                                        size="small"
                                        onClick={() => openPdfInNewTab(pdfPreview)}
                                        dir={direction}

                                    >
                                        <Tooltip title={t('ClickToOpenInFullScreen') || 'Click to open in full screen'} >
                                            <IconButton
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '30px',
                                                    height: '30px',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 1)',
                                                    },
                                                }}
                                                size="small"
                                                onClick={() => openPdfInNewTab(pdfPreview)}

                                            >
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>

                            {previewMode === 'html' ? (
                                <DocumentPreview
                                    t={t}
                                    key={`preview-${JSON.stringify(settings.columnSettings)}-${selectedDocumentType}-${signaturePreview ? 'sig' : 'nosig'}-${profilePreview ? 'prof' : 'noprof'}-${settings.customDescription ? 'desc' : 'nodesc'}-${settings.customFooter ? 'footer' : 'nofooter'}-${settings.columnSettings.upperPanelColor}-${settings.columnSettings.svgImageColor}-${settings.columnSettings.companyInfoTextSize}-${settings.columnSettings.documentTitleTextSize}-${settings.columnSettings.logoPosition}-${settings.columnSettings.totalsLayout}-${defaultItem?.makat ?? 'nodef'}`}
                                    client={client}
                                    settings={settings}
                                    selectedDocumentType={selectedDocumentType}
                                    signaturePreview={signaturePreview}
                                    profilePreview={profilePreview}
                                    direction={direction}
                                    defaultItem={defaultItem}
                                />
                            ) : (
                                <>
                                    {pdfLoading ? (
                                        <Box sx={{
                                            display: 'flex',
                                            height: '100%',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px'
                                        }} dir={direction}>
                                            <Box sx={{ textAlign: 'center' }} dir={direction}>
                                                <CircularProgress sx={{ color: '#1976d2', mb: 1 }} />
                                                <Typography sx={{ color: '#666666', fontSize: '14px' }} dir={direction}>
                                                    {t('GeneratingPreview') || 'Generating preview...'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : pdfPreview ? (
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'flex-start',
                                            padding: '20px',
                                            backgroundColor: '#f8f9fa'
                                        }} dir={direction}>
                                            <Box sx={{
                                                width: '210mm',
                                                minHeight: '200mm',
                                                backgroundColor: '#ffffff',
                                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                position: 'relative'
                                            }}
                                            >


                                                <iframe
                                                    src={pdfPreview}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        border: 'none',
                                                        minHeight: '293mm',
                                                        pointerEvents: 'none'
                                                    }}
                                                    title="PDF Preview"
                                                />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            border: '2px dashed #e8eaed',
                                            borderRadius: '16px',
                                            p: 4,
                                            backgroundColor: '#f8f9fa'
                                        }} dir={direction}>
                                            <DescriptionIcon sx={{
                                                fontSize: 48,
                                                color: '#cccccc',
                                                mb: 2
                                            }} />
                                            <Typography sx={{
                                                color: '#666666',
                                                mb: 3,
                                                fontSize: '16px',
                                                textAlign: 'center'
                                            }} dir={direction}>
                                                {t('PDFPreviewWillAppearHere') || 'PDF preview will appear here'}
                                            </Typography>
                                            <StyledButton
                                                variant="contained"
                                                size="medium"
                                                onClick={() => {
                                                    setPreviewMode('pdf');
                                                    generatePdfPreview();
                                                }}
                                                disabled={pdfLoading}
                                                dir={direction}
                                            >
                                                {pdfLoading ? t('Generating') : t('GeneratePreview')}
                                            </StyledButton>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{
                    p: 2,
                    gap: 1,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #e8eaed'
                }} dir={direction}>
                    <StyledButton
                        onClick={onClose}
                        variant="outlined"
                        dir={direction}
                        size="medium"
                        sx={{ minWidth: '100px' }}
                    >
                        {t('Cancel')}
                    </StyledButton>
                    <StyledButton
                        onClick={handleSave}
                        variant="contained"
                        startIcon={<Save />}
                        dir={direction}
                        size="medium"
                        disabled={loading}
                        sx={{ minWidth: '120px' }}
                    >
                        {loading ? t('Saving') : t('Save')}
                    </StyledButton>
                </DialogActions>
            </Dialog>

            {/* Create client item modal (from Default item section) - in portal above Dialog so it's focusable */}
            {createItemModalOpen && (
                <Modal open disableAutoFocus disableEnforceFocus sx={{ zIndex: 1400 }}>
                    <ClientItemCreateModal
                        open={createItemModalOpen}
                        onClose={() => setCreateItemModalOpen(false)}
                        onSave={handleSaveNewClientItem}
                        direction={direction}
                        t={t}
                        existingItems={(clientItems || []).map((item) => ({ ...item, makat: item.makat ?? item.Makat }))}
                    />
                </Modal>
            )}

            {/* DraggableModal for PDF Preview */}
            <DraggableModal
                bigIframeModalOpen={bigIframeModalOpen}
                openedIframeUrl={openedIframeUrl}
                handleCloseIframeModal={handleCloseIframeModal}
            />
        </>
    );
};

export default DocumentSettingsDialog;
